import { InfoCircleFilled } from '@ant-design/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AspectRatio,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Select,
  Skeleton,
  Stack,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { message } from 'antd';
import AuthLoginModal from 'components/auth/AuthLoginModal';
import { IAppBreadcrumbItem } from 'components/breadcrumb/AppBreadcrumb';
import InputNumber from 'components/inputs/InputNumber';
import PageHeader from 'components/page-header/PageHeader';
import ProductBadge from 'components/products/ProductBadge';
import ProductGallerySwiper from 'components/products/ProductGallerySwiper';
import UtilPriceFormatter from 'components/utils/UtilPriceFormatter';
import { IPaginationResponse } from 'dtos/pagination.dto';
import { IQueryFindOneDto } from 'dtos/query.dto';
import useCart from 'hooks/useCart';
import useCatchError from 'hooks/useCatchError';
import useProduct from 'hooks/useProduct';
import useUser from 'hooks/useUser';
import { IMedia } from 'interfaces/media.interface';
import { IProductAttribute } from 'interfaces/product-attribute.interface';
import {
  IProduct,
  IProductVariation,
  ProductStatusEnum,
} from 'interfaces/product.interface';
import { IWishlist } from 'interfaces/wishlist.interface';
import RootLayout from 'layouts/RootLayout';
import { uniqBy } from 'lodash';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import Image from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { NextPageWithLayout } from 'pages/_app';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CartRepository } from 'respository/cart.repository';
import UserRepository from 'respository/user.repository';
import { useCartStore } from 'store/cart.store';
import { useProductStore } from 'store/product.store';
import { useWishlistStore } from 'store/wishlist.store';
import useSWR from 'swr';
import { v4 as uuidv4 } from 'uuid';

type ProductDetailPageProps = {
  productProp: IProduct | null;
};

export interface IProductAttributeOptionSelected {
  attributeId: number;
  attributeName: string;
  optionId: number;
  optionName: string;
}

export interface IProductGallary extends IMedia {
  uniqueId: number; // Is mean product id or variation id to show in gallery
}

const productQueryObject: IQueryFindOneDto = {
  include: [
    'featuredImage',
    'variations',
    'mainCategory',
    'breadcrumbCategories',
    'attributes',
    'attributeOptions',
    'tags',
    'gallery',
    'productBadge',
  ],
};

const ProductDetailPage: NextPageWithLayout<ProductDetailPageProps> = ({
  productProp,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation(['product']);

  // use router
  const router = useRouter();

  const { locale } = router;

  // use media query
  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const [isTablet] = useMediaQuery('(min-width: 768px)');
  const [isDesktop] = useMediaQuery('(min-width: 1024px)');

  // use user
  const { user } = useUser();

  // use cart
  const { cart, cartUpdate, cartMutate } = useCart();

  // use cart store
  const { setCart } = useCartStore();

  // use product
  const { product } = useProduct({
    permalink: router.query.slug as string,
    queryStringify: queryString.stringify(productQueryObject),
  });

  // use notificaiton
  const [api, contextHolder] = message.useMessage();

  // use catchError
  const catchError = useCatchError();

  // get product detail prebreadcrumb
  const productDetailPreBreadcrumb = useProductStore(
    (state) => state.productDetailPreBreadcrumb
  );

  // use wishlist store
  const { wishlistsTotal, addWishlist, deleteWishlist, setWishlistsTotal } =
    useWishlistStore();

  // get wishlist
  const { data: wishlistResponse } = useSWR<IWishlist>(() =>
    user && user.isAuthenticated && productProp?.id
      ? {
          url: `${process.env.NEXT_PUBLIC_API_URL}/v1/public/profile/wishlists/products/${productProp.id}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      : null
  );

  // init wishlist
  const [wishlist, setWishlist] = useState<IWishlist | undefined>(
    wishlistResponse
  );

  // init wishlist loading
  const [wishlistLoading, setWishlistLoading] = useState<boolean>(false);

  // init product attributes formatted
  const [productAttributesFormatted, setProductAttributesFormatted] = useState<
    IProductAttribute[]
  >([]);

  // init current product variation selected
  const [currentProductVariation, setCurrentProductVariation] =
    useState<IProductVariation>();

  // init current attribute option selected
  const [currentAttributeOptionSelected, setCurrentAttributeOptionSelected] =
    useState<IProductAttributeOptionSelected[]>([]);

  // init quantity
  // ** NOTE: set default value to 1
  const [quantity, setQuantity] = useState(1);

  // init breadcrumbs
  const [breadcrumbs, setBreadcrumbs] = useState<IAppBreadcrumbItem[]>([]);

  // init sale price
  const [salePrice, setSalePrice] = useState<number>(0);

  // init regular price
  const [regularPrice, setRegularPrice] = useState<number>(0);

  // init is out of stock
  const [isOutOfStock, setIsOutOfStock] = useState<boolean>(false);

  // init login modal visible
  const [loginModalVisible, setLoginModalVisible] = useState<boolean>(false);

  // init add to cart loading
  const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);

  // ** set wishlist
  useEffect(() => {
    if (wishlistResponse) {
      setWishlist(wishlistResponse);
    }
  }, [wishlistResponse]);

  // ** set product breadcrumb categories
  useEffect(() => {
    if (product) {
      if (product.breadcrumbCategories && product.mainCategory) {
        const breadcrumbCategory =
          product?.breadcrumbCategories[
            product?.breadcrumbCategories.length - 1
          ];

        const _breadcrumb = [
          {
            label: t('breadcrumb.home', {
              // TH: หน้าแรก
              // EN: Home
              ns: 'common',
            }),
            href: `/`,
          },
        ];

        _breadcrumb.push({
          label: product.mainCategory.name_th,
          href: `/product-categories/${product.mainCategory.permalink}`,
        });

        if (breadcrumbCategory) {
          _breadcrumb.push({
            label: breadcrumbCategory.name_th,
            href: `/product-categories/${product.mainCategory.permalink}/${breadcrumbCategory.permalink}`,
          });
        }

        if (product.name_th) {
          _breadcrumb.push({
            label: product.name_en,
            href: '',
          });
        }

        setBreadcrumbs(_breadcrumb);
      }

      if (productDetailPreBreadcrumb && productDetailPreBreadcrumb.length > 0) {
        const _breadcrumb = uniqBy([...productDetailPreBreadcrumb], 'href');

        _breadcrumb.push({
          label: product.name_en,
          href: '',
        });

        setBreadcrumbs(_breadcrumb);
      }
    }
  }, [product, productDetailPreBreadcrumb, router]);

  // ** set product attributes formatted on mount
  useEffect(() => {
    if (
      product &&
      product.attributes &&
      product.attributeOptions &&
      product.attributeOrders
    ) {
      // sort productAttributeOrder by sequence asc
      const sortedProductAttributeOrder = product.attributeOrders.sort(
        (a, b) => a.sequence - b.sequence
      );

      const _productAttributes =
        product.attributes?.filter((attribute) => attribute) || [];

      // map productAttributeOrder to productAttribute
      const productAttributesSorted = sortedProductAttributeOrder
        .map((productAttributeOrder) => {
          const productAttribute = _productAttributes.find(
            (productAttribute) =>
              productAttribute?.id === productAttributeOrder?.attributeId
          );

          return productAttribute;
        })
        .filter(
          (productAttribute) => productAttribute !== undefined
        ) as IProductAttribute[];

      // format product attributes
      const _productAttributesFormatted = productAttributesSorted
        .map((attribute) => {
          const _options = product.attributeOptions
            ? product.attributeOptions.filter(
                (option) => option?.productAttributeId === attribute?.id
              )
            : [];

          // sort productAttributeOption by sequence asc
          const sortedProductAttributeOption = _options.sort(
            (a, b) => a.sequence - b.sequence
          );

          return {
            ...attribute,
            options: sortedProductAttributeOption,
          };
        })
        .filter((attribute) => attribute !== undefined) as IProductAttribute[];

      // set product attributes formatted
      setProductAttributesFormatted(_productAttributesFormatted);
    }
  }, [product]);

  // ** if product variation is not empty
  // ** set default setCurrentAttributeOptionSelected by first product variation
  useEffect(() => {
    /* if (
      product &&
      product.variations &&
      product.variations.length > 0 &&
      productAttributesFormatted &&
      productAttributesFormatted.length > 0 &&
      !currentAttributeOptionSelected.length
    ) {
      console.log('productAttributesFormatted', productAttributesFormatted);

      const firstProductVariation = product.variations[0];

      if (
        firstProductVariation &&
        firstProductVariation.attributeOptions &&
        firstProductVariation.attributeOptions.length > 0
      ) {
        const _currentProductVariations = productAttributesFormatted.map(
          (attribute, index) => {
            return {
              attributeId: attribute.id,
              attributeName: attribute.name_th,
              optionId: firstProductVariation!.attributeOptions![index]?.id,
              optionName:
                firstProductVariation!.attributeOptions![index]?.name_th,
            };
          }
        );

        setCurrentAttributeOptionSelected(_currentProductVariations);
      }
    } */
  }, [product, productAttributesFormatted]);

  // ** set product gallery
  const productGallery: IProductGallary[] = useMemo(() => {
    if (product) {
      if (product.variations && product.variations.length > 0) {
        const variationGallery = product.variations.map((variation) => {
          if (variation.gallery) {
            return variation.gallery.map((media) => ({
              ...media,
              uniqueId: variation.id, // this is for swiper can slide to index
            }));
          } else {
            return [];
          }
        });

        const variationGalleryFlat = variationGallery.flat();

        // if product.featuredImage is not null
        // prepend product.featuredImage to variationGallery
        if (product.featuredImage) {
          variationGalleryFlat.unshift({
            ...product.featuredImage,
            uniqueId: product.id,
          });
        }

        return variationGalleryFlat;
      } else {
        // product.gallery is not null
        // prepend product.featuredImage to product.gallery
        if (product.gallery) {
          const _gallery = product.gallery.map((media) => ({
            ...media,
            uniqueId: product.id,
          }));

          if (product.featuredImage) {
            _gallery.unshift({
              ...product.featuredImage,
              uniqueId: product.id,
            });
          }

          return _gallery;
        } else {
          // product.gallery is null
          // return product.featuredImage
          if (product.featuredImage) {
            return [
              {
                ...product.featuredImage,
                uniqueId: product.id,
              },
            ];
          }
        }
      }
    }
    return [];
  }, [product]);

  // ** set current product variation when currentAttributeOptionSelected is changed
  useEffect(() => {
    // log currentAttributeOptionSelected
    console.log(
      'currentAttributeOptionSelected',
      currentAttributeOptionSelected
    );

    if (
      product &&
      product.variations &&
      currentAttributeOptionSelected.length > 0
    ) {
      const _currentProductVariation = product.variations.find(
        (variation) =>
          variation.attributeOptions?.every((attributeOption) =>
            currentAttributeOptionSelected.some(
              (optionSelected) =>
                optionSelected.optionId === attributeOption?.id
            )
          ) &&
          variation.attributeOptions.length ===
            currentAttributeOptionSelected.length
      );

      // log _currentProductVariation
      console.log('_currentProductVariation', _currentProductVariation);

      if (_currentProductVariation) {
        setCurrentProductVariation(_currentProductVariation);
      } else {
        setCurrentProductVariation(undefined);
      }
    } else {
      setCurrentProductVariation(undefined);
    }
  }, [currentAttributeOptionSelected, product]);

  // ** set salePrice, regularPrice, stockQuantity when currentProductVariation is changed
  useEffect(() => {
    console.log('currentProductVariation', currentProductVariation);

    if (product) {
      if (product.salePrice && product.salePrice > 0) {
        setSalePrice(product.salePrice);
      }

      setRegularPrice(product.regularPrice || 0);

      if (product.stockQuantity && product.stockQuantity > 0) {
        setIsOutOfStock(false);
      } else {
        setIsOutOfStock(true);
      }
    }

    if (product?.attributes && product?.attributes?.length > 0) {
      setSalePrice(0);
      setRegularPrice(0);
      setIsOutOfStock(false);
    }

    if (currentProductVariation) {
      if (
        currentProductVariation.salePrice &&
        currentProductVariation.salePrice > 0
      ) {
        setSalePrice(currentProductVariation.salePrice);
      }

      setRegularPrice(currentProductVariation.regularPrice || 0);

      if (
        currentProductVariation.stockQuantity &&
        currentProductVariation.stockQuantity > 0
      ) {
        setIsOutOfStock(false);
      } else {
        setIsOutOfStock(true);
      }
    }
  }, [currentProductVariation, product]);

  // ** handle save or delete wishlist
  const handleSaveOrDeleteWishlist = async () => {
    try {
      setWishlistLoading(true);

      if (!user || !user.isAuthenticated) {
        setLoginModalVisible(true);
        return;
      }

      if (!product) {
        return;
      }

      // ** NOTE: if product is not in wishlist
      // save product to wishlist
      if (!wishlist) {
        const wishlistCreated = await UserRepository.wishListCreate({
          productId: product.id,
          variationId: currentProductVariation
            ? currentProductVariation.id
            : undefined,
          attributeOptionIds: currentProductVariation
            ? currentProductVariation.attributeOptions
              ? currentProductVariation.attributeOptions.map(
                  (option) => option.id
                )
              : []
            : [],
        });

        // log wishlist created
        console.log('wishlistCreated', wishlistCreated);

        setWishlist(wishlistCreated);

        // append wishlist to wishlist store
        addWishlist(wishlistCreated);

        // set wishlist total
        setWishlistsTotal(wishlistsTotal + 1);

        // show success message
        api.success(
          t('product.wishlist.toast.saved.title', {
            // TH: เพิ่มสินค้าเข้ารายการที่ชื่นชอบแล้ว
            // EN: Add product to wishlist successfully
          })
        );
      }
      // ** NOTE: if product is in wishlist
      // delete product from wishlist
      else {
        const wishlistDeleted = await UserRepository.wishListDelete(
          wishlist.id
        );

        // log wishlist deleted
        console.log('wishlistDeleted', wishlistDeleted);

        setWishlist(undefined);

        // remove wishlist from wishlist store
        deleteWishlist(wishlist.id);

        // set wishlist total
        setWishlistsTotal(wishlistsTotal - 1);

        // show success message
        api.success(
          t('product.wishlist.toast.deleted.title', {
            // TH: ลบสินค้าออกจากรายการที่ชอบเรียบร้อยแล้ว
            // EN: Delete product from Wishlist successfully
          })
        );
      }
    } catch (error) {
      catchError(error);
    } finally {
      setWishlistLoading(false);
    }
  };

  // ** handle add to cart
  const handleAddToCart = useCallback(async () => {
    try {
      setAddToCartLoading(true);

      if (!product) {
        return;
      }

      let _productNameFormatted =
        router.locale === 'th' ? product.name_th : product.name_th;

      let _productWeightFormatted = product.weight ?? 0;

      if (currentProductVariation) {
        _productNameFormatted = `${_productNameFormatted} (${currentProductVariation.attributeOptions
          ?.map((option) =>
            router.locale === 'th' ? option.name_th : option.name_en
          )
          .join(' / ')})`;

        _productWeightFormatted = currentProductVariation.weight ?? 0;
      }

      const initialItem = {
        id: 0,
        productId: product.id,
        variationId: currentProductVariation?.id || 0,
        quantity: quantity,
        price: salePrice,
        regularPrice: regularPrice,
        salePrice: salePrice,
        key: uuidv4(),
        cartId: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        product,
        attributeOptions: currentProductVariation?.attributeOptions || [],
        subTotal: quantity * salePrice,
        total: quantity * salePrice,
        selected: true,
        weight: _productWeightFormatted,
        weightUnit: 'g',
        weightTotal: _productWeightFormatted * quantity,
        buy1Get1Id: null,
        hasBuy1Get1: false,
        buyXGetXId: null,
        maxQuantity: 10,
      };

      // ** NOTE: cart is not exist
      // ** create cart
      if (!cart) {
        if (!user || !user.isAuthenticated) {
          cartUpdate({
            sessionId: uuidv4(),
            items: [initialItem],
            id: 0,
            shippingMethodId: 0,
            shippingAddressId: 0,
            discount: null,
            couponDiscountTotal: 0,
            discountTotal: 0,
            percentageOffsPromotionsDiscountTotal: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            shippingTotal: 0,
            subTotal: quantity * salePrice,
            total: quantity * salePrice,
          });
        }
      } else {
        // if currentProductVariation
        if (currentProductVariation) {
          // check stock quantity
          if (
            currentProductVariation.stockQuantity &&
            currentProductVariation.stockQuantity <= 0
          ) {
            setIsOutOfStock(true);
            return;
          }

          const _currentCartItem = cart.items.find(
            (item) =>
              item.productId === product.id &&
              item.attributeOptions?.filter((option) =>
                currentProductVariation?.attributeOptions?.find(
                  (variationOption) => variationOption.id === option.id
                )
              ).length === currentProductVariation?.attributeOptions?.length
          );

          if (user && user.isAuthenticated) {
            // add product to cart
            const cartUpdated = await CartRepository.addOrUpdateItemToCart({
              key: _currentCartItem?.key,
              productId: product.id,
              variationId: currentProductVariation.id,
              quantity: quantity,
              attributeOptionIds: currentProductVariation
                ? currentProductVariation.attributeOptions?.map(
                    (option) => option.id
                  )
                : [],
            });

            setCart({ ...cartUpdated, items: cartUpdated.items });
            cartMutate({ ...cartUpdated, items: cartUpdated.items }, false);
          } else {
            const cartUpdated = {
              ...cart,
              items: _currentCartItem
                ? cart.items.map((item) => {
                    item.selected = true;

                    if (item.key === _currentCartItem!.key) {
                      return {
                        ...item,
                        quantity: item.quantity + quantity,
                      };
                    } else {
                      return item;
                    }
                  })
                : [...cart.items, initialItem],
            };

            setCart(cartUpdated);
          }
        } else {
          // ** NOTE: is product
          // check product stock quantity
          if (product.stockQuantity && product.stockQuantity <= 0) {
            setIsOutOfStock(true);
            return;
          }

          const _currentCartItem = cart.items.find(
            (item) =>
              item.productId === product.id &&
              item.attributeOptions?.length === 0
          );

          if (user && user.isAuthenticated) {
            // add product to cart
            const cartUpdated = await CartRepository.addOrUpdateItemToCart({
              key: _currentCartItem?.key,
              productId: product.id,
              quantity: quantity,
            });

            setCart({ ...cartUpdated, items: cartUpdated.items });
            cartMutate({ ...cartUpdated, items: cartUpdated.items }, false);
          } else {
            const cartUpdated = {
              ...cart,
              items: _currentCartItem
                ? cart.items.map((item) => {
                    item.selected = true;
                    if (item.key === _currentCartItem!.key) {
                      return {
                        ...item,
                        quantity: item.quantity + quantity,
                      };
                    } else {
                      return item;
                    }
                  })
                : [...cart.items, initialItem],
            };

            cartUpdate({ ...cartUpdated, items: cartUpdated.items });
          }
        }
      }

      // show message
      api.success(
        t('product.cart.toast.added.title', {
          // TH: เพิ่มสินค้า {{name}} ลงในตะกร้าแล้ว
          // EN: Add {{name}} to cart successfully
          name: _productNameFormatted,
        })
      );
    } catch (error) {
      catchError(error);
    } finally {
      setAddToCartLoading(false);
    }
  }, [
    cart,
    user,
    currentProductVariation,
    product,
    regularPrice,
    salePrice,
    quantity,
  ]);

  if (!product) {
    return (
      <Container
        py={{
          base: 4,
          lg: 16,
        }}
      >
        <Grid
          templateColumns={{
            base: 'minmax(0, 1fr)',
            md: 'minmax(0, 1fr) minmax(0, 1fr)',
            lg: 'minmax(0, 1fr) minmax(0, 1fr)',
          }}
          columnGap={{
            base: 0,
            md: 6,
            lg: 10,
          }}
        >
          <GridItem position="relative">
            <Stack
              position="absolute"
              left="-70px"
              top="0"
              display={{
                base: 'none',
                lg: 'flex',
              }}
            >
              <Skeleton boxSize="50px"></Skeleton>
              <Skeleton boxSize="50px"></Skeleton>
              <Skeleton boxSize="50px"></Skeleton>
              <Skeleton boxSize="50px"></Skeleton>
            </Stack>
            <AspectRatio>
              <Skeleton></Skeleton>
            </AspectRatio>
          </GridItem>
          <GridItem>
            <Flex
              flexDirection="column"
              height="100%"
              justifyContent="space-between"
              minHeight="250px"
            >
              <Box>
                <Skeleton height="20px" mb={4}></Skeleton>
                <Skeleton height="15px" width="70%"></Skeleton>
              </Box>

              <HStack>
                <Box flex={1}>
                  <Skeleton height="50px"></Skeleton>
                </Box>
                <Box flex={1}>
                  <Skeleton height="50px"></Skeleton>
                </Box>
                <Box flex={1}>
                  <Skeleton height="50px"></Skeleton>
                </Box>
              </HStack>
            </Flex>
          </GridItem>
        </Grid>
      </Container>
    );
  }

  if (product.status !== ProductStatusEnum.PUBLISHED) {
    return (
      <Container
        py={{
          base: 8,
          lg: 20,
        }}
      >
        <Center>
          <Text>
            {t('product.detail.notFound', {
              // TH: ไม่พบสินค้า
              // EN: Product not found
            })}
          </Text>
        </Center>
      </Container>
    );
  }

  const maxQuantity = currentProductVariation
    ? currentProductVariation.stockQuantity
    : product.stockQuantity || 10;

  return (
    <>
      <Head>
        <title>
          {product && locale === 'th' ? product.name_th : product.name_en}
        </title>
        <meta name="description" content={product?.description_th ?? ''} />
      </Head>

      {contextHolder}
      <Box>
        <Box
          display={{
            base: 'none',
            md: 'block',
          }}
        >
          <PageHeader breadcrumb={breadcrumbs} />
        </Box>
        <Container
          pt={{
            base: 0,
            md: 2,
            lg: 12,
          }}
          pb={{
            base: 6,
            md: 10,
            lg: 12,
          }}
        >
          <Box
            ml={{
              base: 0,
              lg: '0',
            }}
          >
            <Grid
              templateColumns={{
                base: 'minmax(0, 1fr)',
                md: 'minmax(0, 1fr) minmax(0, 1fr)',
                lg: 'minmax(0, 1fr) minmax(0, 1fr)',
              }}
              columnGap={{
                base: 0,
                md: 6,
                lg: 10,
              }}
            >
              <GridItem>
                <Box
                  pr={{
                    base: 0,
                    lg: 0,
                  }}
                  mb={{
                    base: 6,
                    md: 0,
                  }}
                  mx={{
                    base: -4,
                    md: 0,
                  }}
                  position="relative"
                >
                  {product.productBadge ? (
                    <Center
                      position="absolute"
                      top="0"
                      left={3}
                      zIndex="docked"
                      px={1}
                      minH="44.1px"
                      borderBottomRadius="4px"
                    >
                      <ProductBadge
                        imageName={product.productBadge.imageName ?? ''}
                      ></ProductBadge>
                    </Center>
                  ) : product.isNewArrival ? (
                    <Center
                      position="absolute"
                      top="0"
                      left={3}
                      zIndex="docked"
                      px={1}
                      minH="44.1px"
                      borderBottomRadius="4px"
                    >
                      <ProductBadge type="new"></ProductBadge>
                    </Center>
                  ) : product.isBestSeller ? (
                    <Center
                      position="absolute"
                      top="0"
                      left={3}
                      zIndex="docked"
                      px={1}
                      minH="44.1px"
                      borderBottomRadius="4px"
                    >
                      <ProductBadge type="best-seller"></ProductBadge>
                    </Center>
                  ) : product.isOnSale ? (
                    <Center
                      position="absolute"
                      top="0"
                      left={3}
                      zIndex="docked"
                      px={1}
                      minH="44.1px"
                      borderBottomRadius="4px"
                    >
                      <ProductBadge type="special-price"></ProductBadge>
                    </Center>
                  ) : null}
                  {/* {salePrice > 0 && (
                    <Center
                      position="absolute"
                      top="0"
                      left={4}
                      bg="#eb4123"
                      color="white"
                      zIndex="docked"
                      px={1}
                      minH="40px"
                      borderBottomRadius="8px"
                    >
                      <Text
                        textTransform="uppercase"
                        fontWeight={400}
                        fontSize="lg"
                      >
                        Sale
                      </Text>
                    </Center>
                  )} */}
                  <ProductGallerySwiper
                    product={product}
                    currentProductVariation={currentProductVariation}
                    images={productGallery}
                  ></ProductGallerySwiper>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  pb={{
                    base: 0,
                    md: 6,
                  }}
                  mb={{
                    base: 4,
                    md: 6,
                  }}
                  borderBottom={{
                    md: '1px solid #aaa',
                  }}
                >
                  <Heading
                    fontSize={{
                      base: '30px',
                      md: '32px',
                      lg: '36px',
                    }}
                    textTransform="uppercase"
                    fontWeight="400"
                    color="gray.800"
                  >
                    {locale === 'th'
                      ? product.name_th
                        ? product.name_th
                        : product.name_en
                      : product.name_en}
                  </Heading>
                </Box>
                <Box
                  mb={6}
                  pb={5}
                  display={{
                    base: 'block',
                    md: 'none',
                  }}
                  borderBottom="1px solid #aaa"
                >
                  <HStack spacing={3} alignItems="baseline">
                    {salePrice > 0 || regularPrice > 0 ? (
                      <>
                        <Text
                          fontSize="40px"
                          lineHeight="40px"
                          fontWeight="400"
                          color="primary.500"
                        >
                          <UtilPriceFormatter
                            price={salePrice > 0 ? salePrice : regularPrice}
                            format="0,0"
                          ></UtilPriceFormatter>
                        </Text>
                        {salePrice > 0 && regularPrice > 0 && (
                          <Text as="s" fontSize="20px" color="gray.400">
                            <UtilPriceFormatter
                              price={regularPrice}
                            ></UtilPriceFormatter>
                          </Text>
                        )}
                      </>
                    ) : currentProductVariation ? (
                      <HStack>
                        <Text color="red.300">
                          <InfoCircleFilled></InfoCircleFilled>
                        </Text>
                        <Text color="red.500">
                          {t('product.general.notAvailable', {
                            // TH: สินค้านี้ยังไม่พร้อม กรุณาติดต่อเจ้าหน้าที่
                            // EN: This product is not ready yet. Please contact the staff.
                          })}
                        </Text>
                      </HStack>
                    ) : null}
                  </HStack>
                </Box>
                {productAttributesFormatted &&
                  productAttributesFormatted.length > 0 && (
                    <Box
                      pb={6}
                      mb={6}
                      borderBottom="1px solid"
                      borderColor="#aaa"
                    >
                      <HStack
                        alignItems="flex-start"
                        spacing={{
                          base: 0,
                          lg: 6,
                        }}
                        flexDirection={{
                          base: 'column',
                          lg: 'row',
                        }}
                      >
                        {productAttributesFormatted &&
                          productAttributesFormatted.map(
                            (attribute, index: number) => (
                              <HStack
                                key={attribute.id}
                                flex="1"
                                maxWidth={{
                                  base: '100%',
                                  lg: '50%',
                                }}
                                width={{
                                  base: '100%',
                                  lg: '50%',
                                }}
                                spacing={{
                                  base: 0,
                                  lg: 4,
                                }}
                                flexDirection={{
                                  base: 'column',
                                  lg: 'row',
                                }}
                                alignItems={{
                                  base: 'flex-start',
                                  lg: 'center',
                                }}
                                mb={{
                                  base: 4,
                                  lg: 0,
                                }}
                              >
                                <Text flex="0 0 auto">
                                  {router.locale === 'th'
                                    ? attribute.name_th
                                    : attribute.name_en}
                                  {' : '}
                                </Text>
                                <Box
                                  flex={1}
                                  w={{
                                    base: '100%',
                                    lg: 'auto',
                                  }}
                                >
                                  <Select
                                    size={{
                                      base: 'md',
                                      lg: 'sm',
                                    }}
                                    fontSize="md"
                                    icon={
                                      <Center
                                        width="16px !important"
                                        height="10px !important"
                                        mr={-2}
                                      >
                                        <Image
                                          src="/image-icon/dropdown.png"
                                          width={16}
                                          height={10}
                                          alt="dropdown"
                                        ></Image>
                                      </Center>
                                    }
                                    value={
                                      currentAttributeOptionSelected.find(
                                        (option) =>
                                          option.attributeId === attribute.id
                                      )?.optionId
                                    }
                                    placeholder="---"
                                    onChange={(e) => {
                                      // set current attribute option selected
                                      // if not exist, push to array
                                      // if exist, update value
                                      const index =
                                        currentAttributeOptionSelected.findIndex(
                                          (option) =>
                                            option.attributeId === attribute.id
                                        );

                                      if (index !== -1) {
                                        currentAttributeOptionSelected[index] =
                                          {
                                            attributeId: attribute.id,
                                            attributeName: attribute.name_th,
                                            optionId: parseInt(e.target.value),
                                            optionName:
                                              attribute.options?.find(
                                                (option) =>
                                                  option.id ===
                                                  parseInt(e.target.value)
                                              )?.name_th ?? '',
                                          };
                                      } else {
                                        currentAttributeOptionSelected.push({
                                          attributeId: attribute.id,
                                          attributeName: attribute.name_th,
                                          optionId: parseInt(e.target.value),
                                          optionName:
                                            attribute.options?.find(
                                              (option) =>
                                                option.id ===
                                                parseInt(e.target.value)
                                            )?.name_th ?? '',
                                        });
                                      }

                                      // set current attribute option selected
                                      setCurrentAttributeOptionSelected([
                                        ...currentAttributeOptionSelected,
                                      ]);
                                    }}
                                  >
                                    {attribute.options?.map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {router.locale === 'th'
                                          ? option.name_th
                                          : option.name_en}
                                      </option>
                                    ))}
                                  </Select>
                                </Box>
                              </HStack>
                            )
                          )}
                      </HStack>
                    </Box>
                  )}

                <Box pb={6} mb={6} borderBottom="1px solid" borderColor="#aaa">
                  <HStack
                    maxW={{
                      base: '100%',
                    }}
                    spacing={4}
                    justifyContent={{
                      base: 'space-between',
                      lg: 'flex-start',
                    }}
                  >
                    <Text>
                      {t('product.label.amount', {
                        // TH: จำนวน
                        // EN: Amount
                      })}
                    </Text>
                    {!isOutOfStock ? (
                      <Box flex="0 0 100px">
                        <InputNumber
                          step={1}
                          min={1}
                          max={maxQuantity || 0}
                          value={quantity}
                          isDisabled={isOutOfStock}
                          onChange={(value) => setQuantity(+value)}
                        ></InputNumber>
                      </Box>
                    ) : (
                      <HStack mt={3}>
                        <Text color="red.300">
                          <InfoCircleFilled></InfoCircleFilled>
                        </Text>
                        <Text color="red.500">
                          {t('product.label.general.outOfStock', {
                            // TH: สินค้าหมด ติดต่อเจ้าหน้าที่ หรือ สั่งซื้อสินค้าอื่น
                            // EN: Out of stock, please contact us or order other products
                          })}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                </Box>

                <Box
                  mb={6}
                  display={{
                    base: 'none',
                    md: 'block',
                  }}
                >
                  <HStack spacing={3} alignItems="baseline">
                    {salePrice > 0 || regularPrice > 0 ? (
                      <>
                        <Text
                          fontSize="54px"
                          lineHeight="50px"
                          fontWeight="400"
                          color="primary.500"
                        >
                          <UtilPriceFormatter
                            price={salePrice > 0 ? salePrice : regularPrice}
                            format="0,0"
                          ></UtilPriceFormatter>
                        </Text>
                        {salePrice > 0 &&
                          regularPrice > 0 &&
                          salePrice !== regularPrice && (
                            <Text as="s" fontSize="24px" color="gray.400">
                              <UtilPriceFormatter
                                price={regularPrice}
                              ></UtilPriceFormatter>
                            </Text>
                          )}
                      </>
                    ) : currentProductVariation ? (
                      <HStack>
                        <Text color="red.300">
                          <InfoCircleFilled></InfoCircleFilled>
                        </Text>
                        <Text color="red.500">
                          {t('product.general.notAvailable', {
                            // TH: สินค้านี้ยังไม่พร้อม กรุณาติดต่อเจ้าหน้าที่
                            // EN: This product is not ready yet. Please contact the staff.
                          })}
                        </Text>
                      </HStack>
                    ) : null}
                  </HStack>
                </Box>

                {isTablet && (
                  <Box>
                    <HStack>
                      <Box flex="1">
                        <Button
                          height="44px"
                          borderRadius="4px"
                          width="100%"
                          disabled={
                            (salePrice === 0 && regularPrice === 0) ||
                            isOutOfStock
                          }
                          className="add-to-cart-button"
                          onClick={handleAddToCart}
                          isLoading={addToCartLoading}
                        >
                          <HStack>
                            <Box flex="0 0 20px">
                              <Image
                                src="/image-icon/shopping-bag-white.png"
                                alt="shopping-bag"
                                width={32}
                                height={42}
                              ></Image>
                            </Box>
                            <Text
                              flex={1}
                              fontWeight="500"
                              textTransform="uppercase"
                            >
                              {t('product.button.addToCart', {
                                // TH: ใส่ตะกร้า
                                // EN: Add to cart
                              })}
                            </Text>
                          </HStack>
                        </Button>
                      </Box>
                      <Box flex="0 0 auto">
                        <Button
                          height="44px"
                          borderRadius="4px"
                          colorScheme="red"
                          onClick={handleSaveOrDeleteWishlist}
                          isLoading={wishlistLoading}
                        >
                          <Image
                            src={
                              wishlist
                                ? '/image-icon/wishlist-active.png'
                                : '/image-icon/wishlist-inactive.png'
                            }
                            alt="shopping-bag"
                            width={30}
                            height={28}
                          ></Image>
                        </Button>
                      </Box>
                      <Box
                        flex={1}
                        display={{
                          base: 'none',
                          lg: 'block',
                        }}
                      >
                        <NextLink
                          href={`https://line.me/R/ti/p/@lamptan`}
                          target="_blank"
                          legacyBehavior
                          passHref
                        >
                          <Button
                            height="44px"
                            borderRadius="4px"
                            bg="#06C755"
                            px={1}
                            _hover={{ bg: '#05ac49' }}
                            _active={{ bg: '#05ac49' }}
                            as="a"
                            target={'_blank'}
                          >
                            <HStack spacing={1}>
                              <Box flex="0 0 22px" pt={1}>
                                <Image
                                  src="/image-icon/line-icon-button.png"
                                  alt="shopping-bag"
                                  width={28}
                                  height={26}
                                ></Image>
                              </Box>
                              <Text
                                flex="1"
                                fontSize={locale === 'th' ? 'md' : 'sm'}
                              >
                                {t('product.button.line', {
                                  // TH: แชท + ซื้อผ่านไลน์
                                  // EN: Chat + Buy on Line
                                })}
                              </Text>
                            </HStack>
                          </Button>
                        </NextLink>
                      </Box>
                    </HStack>
                  </Box>
                )}

                {!isDesktop && (
                  <Box
                    mt={{
                      base: 0,
                      md: 4,
                    }}
                  >
                    <NextLink
                      href={`https://line.me/R/ti/p/@lamptan`}
                      target="_blank"
                      legacyBehavior
                      passHref
                    >
                      <Button
                        height="44px"
                        borderRadius="4px"
                        bg="#06C755"
                        px={1}
                        _hover={{ bg: '#05ac49' }}
                        _active={{ bg: '#05ac49' }}
                        width="100%"
                        as="a"
                        target={'_blank'}
                      >
                        <HStack>
                          <Box flex="0 0 23px" pt={1}>
                            <Image
                              src="/image-icon/line-icon-button.png"
                              alt="shopping-bag"
                              width={32}
                              height={30}
                            ></Image>
                          </Box>
                          <Text flex="1">
                            {t('product.button.line', {
                              // TH: แชท + ซื้อผ่านไลน์
                              // EN: Chat + Buy on Line
                            })}
                          </Text>
                        </HStack>
                      </Button>
                    </NextLink>
                  </Box>
                )}
              </GridItem>
            </Grid>
          </Box>

          {product.description_th && (
            <Box
              borderTop="1px solid"
              borderColor="#aaa"
              mt={{
                base: 6,
                lg: 8,
              }}
              pt={7}
            >
              <Heading
                fontSize="2xl"
                mb={4}
                fontWeight="400"
                color="gray.800"
                textTransform="uppercase"
              >
                {t('product.detail.description.title', {
                  // TH: รายละเอียดสินค้า
                  // EN: Product Details
                })}
              </Heading>
              <Box
                className="entry-content"
                dangerouslySetInnerHTML={{
                  __html: product.description_th ?? '',
                }}
              ></Box>
            </Box>
          )}

          {product.brochure && (
            <Box borderTop="1px solid" borderColor="#aaa" mt={8} pt={7}>
              <Box
                dangerouslySetInnerHTML={{ __html: product.brochure ?? '' }}
              ></Box>
            </Box>
          )}

          {product.manualGuide && (
            <Box mt={8}>
              <Accordion allowMultiple>
                <AccordionItem border="0">
                  <AccordionButton
                    bg="#29241E"
                    color="white"
                    _hover={{
                      bg: '#29241E',
                      color: 'white',
                    }}
                    fontSize="28px"
                    fontWeight={200}
                  >
                    <Box flex="1" textAlign="left" textTransform="uppercase">
                      {t('product.detail.manualGuide.title', {
                        // TH: คู่มือการใช้งาน
                        // EN: Manual Guide
                      })}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    px={0}
                    pt={{
                      base: 6,
                      lg: 4,
                    }}
                  >
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: product.manualGuide ?? '',
                      }}
                    ></Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          )}
        </Container>
      </Box>

      <AuthLoginModal
        visible={loginModalVisible}
        onClose={() => setLoginModalVisible(false)}
      ></AuthLoginModal>

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        w="100%"
        display={{
          base: 'block',
          lg: 'none',
        }}
      >
        <HStack spacing={0}>
          <Box flex="0 0 auto">
            <Button
              height="60px"
              borderRadius="0px"
              colorScheme="red"
              onClick={handleSaveOrDeleteWishlist}
              isLoading={wishlistLoading}
            >
              <Image
                src={
                  wishlist
                    ? '/image-icon/wishlist-active.png'
                    : '/image-icon/wishlist-inactive.png'
                }
                alt="shopping-bag"
                width={30}
                height={28}
              ></Image>
            </Button>
          </Box>
          <Box flex="1">
            <Button
              height="60px"
              borderRadius="0px"
              width="100%"
              disabled={(salePrice === 0 && regularPrice === 0) || isOutOfStock}
              fontSize="20px"
              className="add-to-cart-button"
              onClick={handleAddToCart}
              isLoading={addToCartLoading}
            >
              <HStack>
                <Box flex="0 0 20px">
                  <Image
                    src="/image-icon/shopping-bag-white.png"
                    alt="shopping-bag"
                    width={32}
                    height={42}
                  ></Image>
                </Box>
                <Text flex={1} fontWeight="500">
                  {t('product.button.addToCart', {
                    // TH: ใส่ตะกร้า
                    // EN: Add to cart
                  })}
                </Text>
              </HStack>
            </Button>
          </Box>
        </HStack>
      </Box>
    </>
  );
};

ProductDetailPage.getLayout = (page) => {
  return (
    <RootLayout
      pb={{
        base: '50px',
        lg: 0,
      }}
    >
      {page}
    </RootLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  // log process.env.API_URL
  console.log('process.env.API_URL', process.env.API_URL);

  const response = await fetch(
    `${process.env.API_URL}/v1/public/products/?limit=10&include=featuredImage&include=variations`
  );

  const data: IPaginationResponse<IProduct> = await response.json();

  console.log('data.docs', data);

  return {
    paths: data?.docs?.map((doc) => ({
      params: {
        slug: doc.permalink,
      },
    })),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<ProductDetailPageProps> = async ({
  params,
  locale,
}) => {
  const _locale = locale || 'th';

  // get id from params
  const slug = params?.slug;

  // log params?.slug
  console.log('params?.slug', params?.slug);

  // if slug is not found
  if (!slug) {
    return {
      notFound: true,
    };
  }

  let product: IProduct | null = null;

  try {
    // get product
    const response = await fetch(
      `${
        process.env.API_URL
      }/v1/public/products/permalink/${slug}?${queryString.stringify(
        productQueryObject
      )}`
    );

    product = await response.json();
  } catch (error: any) {
    product = null;
  }

  return {
    props: {
      productProp: product,
      ...(await serverSideTranslations(_locale, ['product', 'auth', 'common'])),
      revalidate: 2,
    },
  };
};

export default ProductDetailPage;
